import { Page, View, Text, Image, Document } from '@react-pdf/renderer';
// utils
import { fCurrency } from '@/utils/formatNumber';
import { fDate } from '@/utils/formatTime';
// @types
import { InvoiceAdjustment, ProviderInvoice } from '../../types';
//
import styles from './InvoiceStyle';

// ----------------------------------------------------------------------

type Props = {
  invoice: ProviderInvoice;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    url?: string;
    registration?: string;
  };
};

export default function InvoicePDF({ invoice, companyInfo }: Props) {
  const {
    id,
    due_date,
    total,
    created_at,
    invoice_status,
    provider_invoice_items,
    manual_adjustments,
    base_total,
    adjusted_total,
  } = invoice;

  const adjustments: InvoiceAdjustment[] = manual_adjustments || [];

  const adjustmentsTotal = adjustments.reduce((sum, adj) => {
    const amount = Number(adj.amount) || 0;
    const signed =
      adj.type === 'credit'
        ? -Math.abs(amount)
        : adj.type === 'debit'
        ? Math.abs(amount)
        : amount;
    return sum + signed;
  }, 0);

  const baseTotal = base_total ?? total ?? 0;
  const grandTotal = adjusted_total ?? baseTotal + adjustmentsTotal;

  const normalizeAddress = (address?: string) => {
    if (!address) return undefined;
    return address
      .replace(/<\s*\/?\s*br\s*\/?\s*>/gi, '\n')
      .replace(/\s+\n/g, '\n')
      .trim();
  };

  const providerAddress =
    invoice.users?.address?.find((addr) => addr.default) ||
    invoice.users?.address?.[0];

  const providerName = `${invoice.users?.first_name ?? ''} ${
    invoice.users?.last_name ?? ''
  }`.trim() || 'Provider';

  const providerContact = {
    name: providerName,
    address: normalizeAddress(providerAddress?.formatted_address),
    phone: invoice.users?.phone,
    email: invoice.users?.email,
  };

  const companyContact = {
    name: companyInfo?.name || 'Get Set Clean',
    address: companyInfo?.address,
    phone: companyInfo?.phone,
    email: companyInfo?.email || 'support@getsetclean.co.uk',
    url: companyInfo?.url,
    registration: companyInfo?.registration,
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={[styles.gridContainer, styles.mb40]}>
          <Image source="/logo/logo_full.jpg" style={{ height: 32 }} />
          <View style={{ alignItems: 'flex-end', flexDirection: 'column' }}>
            <Text style={styles.h3}>{invoice_status.display}</Text>
            <Text> {`INV-${id}`} </Text>
          </View>
        </View>

        <View style={[styles.gridContainer, styles.mb40]}>
          <View style={styles.col6}>
            <Text style={[styles.overline, styles.mb8]}>Invoice from</Text>
            <Text style={styles.body1}>{providerContact.name}</Text>
            {providerContact.address ? (
              <Text style={styles.body1}>{providerContact.address}</Text>
            ) : null}
            {providerContact.phone ? (
              <Text style={styles.body1}>Phone: {providerContact.phone}</Text>
            ) : null}
            {providerContact.email ? (
              <Text style={styles.body1}>Email: {providerContact.email}</Text>
            ) : null}
          </View>

          <View style={styles.col6}>
            <Text style={[styles.overline, styles.mb8]}>Invoice to</Text>
            <Text style={styles.body1}>{companyContact.name}</Text>
            {companyContact.address ? (
              <Text style={styles.body1}>{companyContact.address}</Text>
            ) : null}
            {companyContact.phone ? (
              <Text style={styles.body1}>Phone: {companyContact.phone}</Text>
            ) : null}
            {companyContact.email ? (
              <Text style={styles.body1}>Email: {companyContact.email}</Text>
            ) : null}
            {companyContact.url ? (
              <Text style={styles.body1}>{companyContact.url}</Text>
            ) : null}
            {companyContact.registration ? (
              <Text style={styles.body1}>
                Company No: {companyContact.registration}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={[styles.gridContainer, styles.mb40]}>
          <View style={styles.col6}>
            <Text style={[styles.overline, styles.mb8]}>Date create</Text>
            <Text style={styles.body1}>{fDate(created_at)}</Text>
          </View>
          <View style={styles.col6}>
            <Text style={[styles.overline, styles.mb8]}>Due date</Text>
            <Text style={styles.body1}>{fDate(due_date)}</Text>
          </View>
        </View>

        <Text style={[styles.overline, styles.mb8]}>Invoice Details</Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={styles.tableRow}>
              <View style={styles.tableCell_1}>
                <Text style={styles.subtitle2}>#</Text>
              </View>

              <View style={styles.tableCell_2}>
                <Text style={styles.subtitle2}>Description</Text>
              </View>

              <View style={styles.tableCell_3}>
                <Text style={styles.subtitle2}>Hours</Text>
              </View>

              <View style={styles.tableCell_3}>
                <Text style={styles.subtitle2}>Price/h</Text>
              </View>

              <View style={styles.tableCell_3}>
                <Text style={styles.subtitle2}>Cleaning Subtotal</Text>
              </View>

              <View style={(styles.tableCell_3, styles.alignRight)}>
                <Text style={styles.subtitle2}>Cleaning Products</Text>
              </View>

              <View style={[styles.tableCell_3, styles.alignRight]}>
                <Text style={styles.subtitle2}>Total</Text>
              </View>
            </View>
          </View>

          <View style={styles.tableBody}>
            {provider_invoice_items.map((item, index) => (
              <View style={styles.tableRow} key={item.id}>
                <View style={styles.tableCell_1}>
                  <Text>{index + 1}</Text>
                </View>

                <View style={styles.tableCell_2}>
                  <Text style={styles.subtitle2}>
                    {'#' + item.job_id + ':' + item.description}
                  </Text>
                  <Text>{item.description}</Text>
                </View>

                <View style={styles.tableCell_3}>
                  <Text>{item.hours}</Text>
                </View>

                <View style={styles.tableCell_3}>
                  <Text>{fCurrency(item.price_per_hour)}</Text>
                </View>

                <View style={styles.tableCell_3}>
                  <Text>{fCurrency(item.subtotal)}</Text>
                </View>

                <View style={styles.tableCell_3}>
                  <Text>{fCurrency(item.cleaning_products)}</Text>
                </View>

                <View style={[styles.tableCell_3, styles.alignRight]}>
                  <Text>{fCurrency(item.total)}</Text>
                </View>
              </View>
            ))}

            {adjustments.length ? (
              adjustments.map((adj, index) => {
                const amount = Number(adj.amount) || 0;
                const signedAmount =
                  adj.type === 'credit'
                    ? -Math.abs(amount)
                    : adj.type === 'debit'
                    ? Math.abs(amount)
                    : amount;
                return (
                  <View style={styles.tableRow} key={`adjustment-${index}`}>
                    <View style={styles.tableCell_1}>
                      <Text>{`A${index + 1}`}</Text>
                    </View>
                    <View style={styles.tableCell_2}>
                      <Text style={styles.subtitle2}>{adj.description}</Text>
                      <Text style={styles.caption}>
                        {adj.type === 'credit'
                          ? 'Credit (deduction)'
                          : 'Debit (charge)'}
                      </Text>
                    </View>
                    <View style={styles.tableCell_3}>
                      <Text>—</Text>
                    </View>
                    <View style={styles.tableCell_3}>
                      <Text>—</Text>
                    </View>
                    <View style={styles.tableCell_3}>
                      <Text>—</Text>
                    </View>
                    <View style={styles.tableCell_3}>
                      <Text style={styles.alignRight}>—</Text>
                    </View>
                    <View style={[styles.tableCell_3, styles.alignRight]}>
                      <Text>{fCurrency(signedAmount)}</Text>
                    </View>
                  </View>
                );
              })
            ) : (
              <View style={styles.tableRow} key="no-adjustments">
                <View style={styles.tableCell_1}>
                  <Text />
                </View>
                <View style={styles.tableCell_2}>
                  <Text style={styles.caption}>No manual adjustments</Text>
                </View>
                <View style={styles.tableCell_3}>
                  <Text />
                </View>
                <View style={styles.tableCell_3}>
                  <Text />
                </View>
                <View style={styles.tableCell_3}>
                  <Text />
                </View>
                <View style={styles.tableCell_3}>
                  <Text />
                </View>
                <View style={[styles.tableCell_3, styles.alignRight]}>
                  <Text />
                </View>
              </View>
            )}

            <View style={[styles.tableRow, styles.noBorder]}>
              <View style={styles.tableCell_1} />
              <View style={styles.tableCell_2} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3}>
                <Text style={styles.subtitle2}>Subtotal</Text>
              </View>
              <View style={[styles.tableCell_3, styles.alignRight]}>
                <Text>{fCurrency(baseTotal)}</Text>
              </View>
            </View>

            <View style={[styles.tableRow, styles.noBorder]}>
              <View style={styles.tableCell_1} />
              <View style={styles.tableCell_2} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3}>
                <Text style={styles.subtitle2}>Adjustments</Text>
              </View>
              <View style={[styles.tableCell_3, styles.alignRight]}>
                <Text>{fCurrency(adjustmentsTotal)}</Text>
              </View>
            </View>

            <View style={[styles.tableRow, styles.noBorder]}>
              <View style={styles.tableCell_1} />
              <View style={styles.tableCell_2} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3} />
              <View style={styles.tableCell_3}>
                <Text style={styles.h4}>Total</Text>
              </View>
              <View style={[styles.tableCell_3, styles.alignRight]}>
                <Text style={styles.h4}>{fCurrency(grandTotal)}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.gridContainer, styles.footer]}>
          <View style={styles.col8}>
            <Text style={styles.subtitle2}>NOTES</Text>
            <Text>We appreciate your business.</Text>
          </View>
          <View style={[styles.col4, styles.alignRight]}>
            <Text style={styles.subtitle2}>Have a Question?</Text>
            <Text>support@getsetclean.co.uk</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
